/**
 * NetworkOverview — Collapsible panel housing the Supply-Chain
 * diagram + full Commercial Impact detail.
 *
 * Opened only when the user asks for it. Nothing in here is critical
 * to the primary workflow — it is contextual depth, kept out of the
 * default view to keep each page calm.
 */
import React, { useState } from 'react';
import { ChevronDown, Network } from 'lucide-react';
import SupplyChainFlow from './SupplyChainFlow';
import ImpactSummary from './ImpactSummary';
import { useDistributionDemo } from '../DistributionDemoContext';

export default function NetworkOverview({ intel, sim }) {
    const [open, setOpen] = useState(false);
    const { isSimulating } = useDistributionDemo();

    if (!intel) return null;

    return (
        <div
            className="rounded-lg border border-slate-200 bg-white overflow-hidden"
            data-testid="network-overview"
        >
            <button
                type="button"
                onClick={() => setOpen(v => !v)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
                data-testid="network-overview-toggle"
                aria-expanded={open}
            >
                <div className="flex items-center gap-2">
                    <Network className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-900">Network overview</span>
                    {isSimulating && (
                        <span className="text-[10px] uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5 font-semibold">
                            Scenario active
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span className="hidden sm:inline">
                        {open ? 'Hide' : 'View the supply chain, impact and assumptions'}
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
                </div>
            </button>

            {open && (
                <div className="border-t border-slate-200 p-4 space-y-4 bg-slate-50/40">
                    <SupplyChainFlow nodes={intel.supply_chain || []} />
                    <ImpactSummary
                        projections={sim?.projections || []}
                        impactAssumptions={intel.impact_assumptions || {}}
                        totalRevenueAtRisk={sim?.totalRevenueAtRisk || 0}
                        stockoutsDetected={sim?.stockoutsDetected || 0}
                        idleAtSecondary={sim?.idleAtSecondary || 0}
                    />
                    {intel.impact_assumptions?.note && (
                        <p className="text-[11px] text-slate-500 italic">
                            {intel.impact_assumptions.note}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
